import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

@Component({
  selector: 'anihis-fip-test',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    TranslocoModule,
  ],
  templateUrl: './fip-test.component.html',
  styleUrls: ['./fip-test.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FipTestComponent implements OnInit, OnDestroy {
  @Output() formChange = new EventEmitter<any>();

  form: FormGroup = new FormGroup({
    fipTestResult: new FormGroup({
      en: new FormControl(''),
      sr: new FormControl(''),
    }),
    note: new FormGroup({
      en: new FormControl(''),
      sr: new FormControl(''),
    }),
    veterinarianName: new FormControl(''),
  });

  private subs: Subscription[] = [];
  private translocoService = inject(TranslocoService);
  currentLang: 'en' | 'sr' = 'en';

  ngOnInit(): void {
    const lang =
      (this.translocoService.getActiveLang &&
        this.translocoService.getActiveLang()) ||
      '';
    this.currentLang =
      typeof lang === 'string' && lang.startsWith('sr') ? 'sr' : 'en';

    // emit initial value
    this.formChange.emit(this.form.value);

    // subscribe primary fipTestResult and note fields
    ['fipTestResult', 'note'].forEach((groupName) => {
      const vis = this.form.get([groupName, this.currentLang]);
      const oth = this.form.get([
        groupName,
        this.currentLang === 'en' ? 'sr' : 'en',
      ]);
      if (vis) {
        const s = (vis as any).valueChanges.subscribe((v: any) => {
          if (oth) oth.setValue(v, { emitEvent: false });
          this.formChange.emit(this.form.value);
        });
        this.subs.push(s);
      }
    });

    if ((this.translocoService as any).langChanges$) {
      const langSub = (this.translocoService as any).langChanges$.subscribe(
        () => {
          const newLang =
            (this.translocoService.getActiveLang &&
              this.translocoService.getActiveLang()) ||
            '';
          this.currentLang =
            typeof newLang === 'string' && newLang.startsWith('sr')
              ? 'sr'
              : 'en';
          this.unsubscribeAll();
          ['fipTestResult', 'note'].forEach((groupName) => {
            const vis2 = this.form.get([groupName, this.currentLang]);
            const oth2 = this.form.get([
              groupName,
              this.currentLang === 'en' ? 'sr' : 'en',
            ]);
            if (vis2) {
              const s2 = (vis2 as any).valueChanges.subscribe((v: any) => {
                if (oth2) oth2.setValue(v, { emitEvent: false });
                this.formChange.emit(this.form.value);
              });
              this.subs.push(s2);
            }
          });
        },
      );
      this.subs.push(langSub);
    }

    const vetNameControl = this.form.get('veterinarianName');
    if (vetNameControl) {
      const s = vetNameControl.valueChanges.subscribe(() => {
        this.formChange.emit(this.form.value);
      });
      this.subs.push(s);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  private unsubscribeAll() {
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }
}
